"use client";

import * as React from "react";
import {
	motion,
	useReducedMotion,
	type MotionStyle,
	type Transition,
	type Variants,
} from "motion/react";

import { cn } from "../../lib/utils";

/** The two easing curves the source design uses throughout, as Motion's bezier control points. */
const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];
const EASE_IN_OUT: [number, number, number, number] = [0.77, 0, 0.175, 1];

/** Holds a pose without playing it. Used for the reduced-motion variants. */
const INSTANT: Transition = { duration: 0 };

/**
 * Settles the properties a variant is *not* animating.
 *
 * Every variant restates the full pose, so leaving one state always clears whatever the
 * last one left behind — a card that finishes mid-hop returns to its feet rather than
 * keeping the squash. Short enough to read as a correction, not a second animation.
 */
const SETTLE: Transition = { duration: 0.2, ease: EASE_OUT };

/**
 * Declaration order for pickers, so a chooser lists the shapes the same way everywhere.
 * The union is derived from it, which is what makes {@link SHAPES} exhaustive by construction.
 */
export const AGENT_AVATAR_SHAPES = [
	"hex",
	"clover",
	"arch",
	"diamond",
	"circle",
	"squircle",
] as const;

export type AgentAvatarShape = (typeof AGENT_AVATAR_SHAPES)[number];

/**
 * The palette the shapes are drawn in. Six restrained hues, all dark enough to hold the
 * white eyes.
 *
 * Offered as a default set rather than enforced — `color` takes any CSS colour, because a
 * tenant-themed surface may need its own.
 */
export const AGENT_AVATAR_COLORS = [
	"#7f6df1",
	"#2d879e",
	"#ab653f",
	"#9e2d92",
	"#76a233",
	"#3f3fab",
] as const;

/**
 * The six silhouettes, hoisted so they are not rebuilt on every render.
 *
 * Drawn for this system rather than taken from an icon set: an icon describes the *work*
 * ("inbox" for applications), which duplicates the agent's name and runs out once two
 * agents do similar jobs. These describe the agent instead. Shape is the identity channel
 * because it survives greyscale, colour blindness and a 16px table cell, which six hues do
 * not; colour is personalisation layered on top.
 *
 * They inherit `fill` from the `svg`, so one `color` prop paints whichever is chosen.
 */
const SHAPES: Record<AgentAvatarShape, React.ReactElement> = {
	hex: (
		<path
			d="M12 1.6 21.1 6.8v10.4L12 22.4 2.9 17.2V6.8z"
			data-testid="agent-avatar-shape"
		/>
	),
	clover: (
		<path
			d="M8.4 2.4a5.6 5.6 0 0 1 7.2 0 5.6 5.6 0 0 1 6 6 5.6 5.6 0 0 1 0 7.2 5.6 5.6 0 0 1-6 6 5.6 5.6 0 0 1-7.2 0 5.6 5.6 0 0 1-6-6 5.6 5.6 0 0 1 0-7.2 5.6 5.6 0 0 1 6-6z"
			data-testid="agent-avatar-shape"
		/>
	),
	arch: (
		<path
			d="M2 12a10 10 0 0 1 20 0v7.6A2.4 2.4 0 0 1 19.6 22H4.4A2.4 2.4 0 0 1 2 19.6z"
			data-testid="agent-avatar-shape"
		/>
	),
	diamond: (
		<path
			d="M10.2 2.1a2.6 2.6 0 0 1 3.6 0l8.1 8.1a2.6 2.6 0 0 1 0 3.6l-8.1 8.1a2.6 2.6 0 0 1-3.6 0l-8.1-8.1a2.6 2.6 0 0 1 0-3.6z"
			data-testid="agent-avatar-shape"
		/>
	),
	circle: <circle cx="12" cy="12" r="10" data-testid="agent-avatar-shape" />,
	squircle: (
		<rect
			x="2"
			y="2"
			width="20"
			height="20"
			rx="5.5"
			data-testid="agent-avatar-shape"
		/>
	),
};

export type AgentAvatarState =
	| "idle"
	| "working"
	| "done"
	| "asleep"
	| "failed";

/**
 * Without this an SVG transform is measured against the user-space origin rather than the
 * element, so a blink squashes the eye towards the top-left corner instead of shutting it.
 */
const SVG_SELF_ORIGIN: React.CSSProperties = {
	transformBox: "fill-box",
	transformOrigin: "center",
};

/** The rock and the hop pivot on the avatar's feet rather than its middle. */
const BODY_ORIGIN: React.CSSProperties = { transformOrigin: "50% 100%" };

/** Seconds for one turn of the eye clock. */
const AMBIENT_IDLE_SECONDS = 7.2;

/** Quicker while working — the agent is alert, not resting. */
const AMBIENT_WORKING_SECONDS = 4.4;

/** Shut for a sliver of the cycle and open for the rest, so a blink reads as an event. */
const BLINK_SCALE_Y = [1, 1, 0.1, 1];
const BLINK_TIMES = [0, 0.86, 0.895, 1];

/**
 * Glance right, hold, glance left, hold, settle — and the blink lands on the settle,
 * because both layers run off one duration. Offsetting only one of them would break the
 * sequence into two unrelated tics.
 *
 * Measured in user units, so the glance stays proportionate at any `size` without scaling.
 */
const LOOK_X = [0, 0, 1.5, 1.5, -1.5, -1.5, 0, 0];
const LOOK_TIMES = [0, 0.12, 0.22, 0.34, 0.46, 0.58, 0.68, 1];

/** Rocking rather than bouncing: working is busy, and a celebratory motion would never stop. */
const BUSY_ROTATE = [0, -3.5, 3.5, 0];
const BUSY_TIMES = [0, 0.25, 0.75, 1];

/**
 * A hop with squash and stretch, landing on a smaller rebound. Played twice, because a
 * finish is a moment rather than a mood — it announces itself and stops.
 *
 * The rise is a share of the avatar's own height rather than a flat pixel count, so the hop
 * stays proportionate at any `size`. The design's -6px and -1.5px are against a 26px avatar.
 */
const HOP_Y = ["0%", "0%", "-23.08%", "0%", "-5.77%", "0%"];
const HOP_SCALE_X = [1, 1.08, 0.94, 1.1, 0.99, 1];
const HOP_SCALE_Y = [1, 0.88, 1.1, 0.88, 1.03, 1];
const HOP_TIMES = [0, 0.12, 0.38, 0.66, 0.84, 1];

/** A slow breath, so an agent that is switched off still looks asleep rather than broken. */
const SLEEP_SCALE_X = [1, 1.045, 1];
const SLEEP_SCALE_Y = [1, 0.965, 1];

const ASLEEP_OPACITY = 0.55;

/** Not fully closed: a sliver keeps the eye reading as a shut eye rather than a missing one. */
const CLOSED_EYE_SCALE_Y = 0.14;

/**
 * The eyes drop rather than shut, so a failed agent reads as awake and still. The body
 * stays upright: the lowered eyes alone carry the state.
 */
const FAILED_EYES_Y = 1.2;

/** The pose every state returns to, restated by each variant so none can leave a residue. */
const RESTING_BODY = { rotate: 0, y: "0%", scaleX: 1, scaleY: 1, opacity: 1 };

const BUSY_TRANSITION: Transition = {
	duration: 1.05,
	times: BUSY_TIMES,
	ease: EASE_IN_OUT,
	repeat: Infinity,
};

/** `repeat: 1` is one replay on top of the first pass, which is the two hops the design asks for. */
const HOP_TRANSITION: Transition = {
	duration: 0.62,
	times: HOP_TIMES,
	ease: EASE_OUT,
	repeat: 1,
};

const SLEEP_TRANSITION: Transition = {
	duration: 4.2,
	ease: EASE_IN_OUT,
	repeat: Infinity,
};

/**
 * Per-property transitions rather than one per variant, because each state animates some
 * properties as keyframes and merely resets the others. A single transition would apply the
 * keyframe `times` to the reset properties too, where the counts do not match.
 */
const bodyVariants: Variants = {
	idle: { ...RESTING_BODY, transition: SETTLE },
	working: {
		...RESTING_BODY,
		rotate: BUSY_ROTATE,
		transition: { rotate: BUSY_TRANSITION, default: SETTLE },
	},
	done: {
		...RESTING_BODY,
		y: HOP_Y,
		scaleX: HOP_SCALE_X,
		scaleY: HOP_SCALE_Y,
		transition: {
			y: HOP_TRANSITION,
			scaleX: HOP_TRANSITION,
			scaleY: HOP_TRANSITION,
			default: SETTLE,
		},
	},
	asleep: {
		...RESTING_BODY,
		opacity: ASLEEP_OPACITY,
		scaleX: SLEEP_SCALE_X,
		scaleY: SLEEP_SCALE_Y,
		transition: {
			scaleX: SLEEP_TRANSITION,
			scaleY: SLEEP_TRANSITION,
			default: SETTLE,
		},
	},
	failed: { ...RESTING_BODY, transition: SETTLE },
};

function blinkTransition(duration: number, delay: number): Transition {
	return {
		duration,
		delay,
		times: BLINK_TIMES,
		ease: EASE_OUT,
		repeat: Infinity,
	};
}

function lookTransition(duration: number, delay: number): Transition {
	return {
		duration,
		delay,
		times: LOOK_TIMES,
		ease: EASE_IN_OUT,
		repeat: Infinity,
	};
}

/**
 * Dynamic variants, resolved against the `delay` handed down as Motion's `custom`, so the
 * offset can vary per avatar while the variants themselves stay module-level constants.
 */
const eyeVariants: Variants = {
	idle: (delay: number) => ({
		scaleY: BLINK_SCALE_Y,
		transition: blinkTransition(AMBIENT_IDLE_SECONDS, delay),
	}),
	working: (delay: number) => ({
		scaleY: BLINK_SCALE_Y,
		transition: blinkTransition(AMBIENT_WORKING_SECONDS, delay),
	}),
	done: (delay: number) => ({
		scaleY: BLINK_SCALE_Y,
		transition: blinkTransition(AMBIENT_IDLE_SECONDS, delay),
	}),
	asleep: { scaleY: CLOSED_EYE_SCALE_Y, transition: SETTLE },
	failed: { scaleY: 1, transition: SETTLE },
};

const eyesVariants: Variants = {
	idle: (delay: number) => ({
		x: LOOK_X,
		y: 0,
		transition: {
			x: lookTransition(AMBIENT_IDLE_SECONDS, delay),
			default: SETTLE,
		},
	}),
	working: (delay: number) => ({
		x: LOOK_X,
		y: 0,
		transition: {
			x: lookTransition(AMBIENT_WORKING_SECONDS, delay),
			default: SETTLE,
		},
	}),
	done: (delay: number) => ({
		x: LOOK_X,
		y: 0,
		transition: {
			x: lookTransition(AMBIENT_IDLE_SECONDS, delay),
			default: SETTLE,
		},
	}),
	asleep: { x: 0, y: 0, transition: SETTLE },
	failed: { x: 0, y: FAILED_EYES_Y, transition: SETTLE },
};

/**
 * Reduced motion keeps the *poses* and drops the *movement*.
 *
 * A sleeping agent still has its eyes shut and sits at a lower opacity, because that is a
 * state worth seeing rather than an animation; nothing here moves.
 */
const RESTING_POSE = { ...RESTING_BODY, transition: INSTANT };
const OPEN_EYE = { scaleY: 1, transition: INSTANT };
const STILL_EYES = { x: 0, y: 0, transition: INSTANT };

const staticBodyVariants: Variants = {
	idle: RESTING_POSE,
	working: RESTING_POSE,
	done: RESTING_POSE,
	asleep: { ...RESTING_POSE, opacity: ASLEEP_OPACITY },
	failed: RESTING_POSE,
};

const staticEyeVariants: Variants = {
	idle: OPEN_EYE,
	working: OPEN_EYE,
	done: OPEN_EYE,
	asleep: { scaleY: CLOSED_EYE_SCALE_Y, transition: INSTANT },
	failed: OPEN_EYE,
};

const staticEyesVariants: Variants = {
	idle: STILL_EYES,
	working: STILL_EYES,
	done: STILL_EYES,
	asleep: STILL_EYES,
	failed: { x: 0, y: FAILED_EYES_Y, transition: INSTANT },
};

/**
 * Vinyl moves the body inside the svg rather than the svg itself, so the light and the ground
 * shadow can stay put while the character rocks and hops beneath them.
 *
 * Every moving layer pivots on the same point just above the feet, in view-box units. Sharing
 * it is what lets the highlight's counter-rotation cancel the rock exactly.
 */
const VINYL_PIVOT: MotionStyle = {
	transformBox: "view-box",
	originX: "12px",
	originY: "23px",
};

/** The hop rises out of the view box and the shadow sits on its bottom edge, so neither may be clipped. */
const VINYL_ROOT_STYLE: React.CSSProperties = { overflow: "visible" };

/**
 * Below this the ground shadow is under a pixel and a half tall, and reads as a smudge rather
 * than a shadow.
 */
const VINYL_SHADOW_MIN_SIZE = 18;

/**
 * Idle and working, the body turns with each glance: it narrows as though seen a little side-on
 * and drifts after the eyes, while the sheen slides the other way. Done still glances but does
 * not turn, so the turn never runs on top of the hop. Together they make the light read as fixed in
 * the room rather than painted on. Keyed to {@link LOOK_TIMES}, so the turn and the glance stay
 * one gesture.
 */
const TURN_X = [0, 0, 0.5, 0.5, -0.5, -0.5, 0, 0];
const TURN_SCALE_X = [1, 1, 0.94, 0.94, 0.94, 0.94, 1, 1];
const SHEEN_X = [0, 0, -1.6, -1.6, 1.6, 1.6, 0, 0];

/** {@link BUSY_ROTATE} reversed, so the highlight holds still while the body tips under it. */
const COUNTER_ROTATE = [0, 3.5, -3.5, 0];

/** The shadow narrows and lightens each time the rock tips the body off its feet. */
const GROUND_SCALE_X = [1, 0.9, 1, 0.9, 1];
const GROUND_OPACITY = [0.1, 0.07, 0.1, 0.07, 0.1];
const GROUND_TIMES = [0, 0.25, 0.5, 0.75, 1];

const SHADOW_OPACITY = 0.1;

/** Fainter while asleep, as though the agent has sunk out of the light. */
const ASLEEP_SHADOW_OPACITY = 0.05;

const GROUND_TRANSITION: Transition = {
	...BUSY_TRANSITION,
	times: GROUND_TIMES,
};

const RESTING_TURN = { x: 0, scaleX: 1 };
const RESTING_SHEEN = { x: 0 };
const RESTING_LIGHT = { rotate: 0 };
const RESTING_SHADOW = { scaleX: 1, opacity: SHADOW_OPACITY };

/** On the eye clock, so it takes the same `delay` the eyes do. */
const turnVariants: Variants = {
	idle: (delay: number) => ({
		x: TURN_X,
		scaleX: TURN_SCALE_X,
		transition: lookTransition(AMBIENT_IDLE_SECONDS, delay),
	}),
	working: (delay: number) => ({
		x: TURN_X,
		scaleX: TURN_SCALE_X,
		transition: lookTransition(AMBIENT_WORKING_SECONDS, delay),
	}),
	done: { ...RESTING_TURN, transition: SETTLE },
	asleep: { ...RESTING_TURN, transition: SETTLE },
	failed: { ...RESTING_TURN, transition: SETTLE },
};

const sheenVariants: Variants = {
	idle: (delay: number) => ({
		x: SHEEN_X,
		transition: lookTransition(AMBIENT_IDLE_SECONDS, delay),
	}),
	working: (delay: number) => ({
		x: SHEEN_X,
		transition: lookTransition(AMBIENT_WORKING_SECONDS, delay),
	}),
	done: { ...RESTING_SHEEN, transition: SETTLE },
	asleep: { ...RESTING_SHEEN, transition: SETTLE },
	failed: { ...RESTING_SHEEN, transition: SETTLE },
};

/** On the rock's clock rather than the eyes', so like the rock it takes no `delay`. */
const lightVariants: Variants = {
	idle: { ...RESTING_LIGHT, transition: SETTLE },
	working: { rotate: COUNTER_ROTATE, transition: BUSY_TRANSITION },
	done: { ...RESTING_LIGHT, transition: SETTLE },
	asleep: { ...RESTING_LIGHT, transition: SETTLE },
	failed: { ...RESTING_LIGHT, transition: SETTLE },
};

const shadowVariants: Variants = {
	idle: { ...RESTING_SHADOW, transition: SETTLE },
	working: {
		scaleX: GROUND_SCALE_X,
		opacity: GROUND_OPACITY,
		transition: GROUND_TRANSITION,
	},
	done: { ...RESTING_SHADOW, transition: SETTLE },
	asleep: {
		...RESTING_SHADOW,
		opacity: ASLEEP_SHADOW_OPACITY,
		transition: SETTLE,
	},
	failed: { ...RESTING_SHADOW, transition: SETTLE },
};

const STILL_TURN = { ...RESTING_TURN, transition: INSTANT };
const STILL_SHEEN = { ...RESTING_SHEEN, transition: INSTANT };
const STILL_LIGHT = { ...RESTING_LIGHT, transition: INSTANT };
const STILL_SHADOW = { ...RESTING_SHADOW, transition: INSTANT };

const staticTurnVariants: Variants = {
	idle: STILL_TURN,
	working: STILL_TURN,
	done: STILL_TURN,
	asleep: STILL_TURN,
	failed: STILL_TURN,
};

const staticSheenVariants: Variants = {
	idle: STILL_SHEEN,
	working: STILL_SHEEN,
	done: STILL_SHEEN,
	asleep: STILL_SHEEN,
	failed: STILL_SHEEN,
};

const staticLightVariants: Variants = {
	idle: STILL_LIGHT,
	working: STILL_LIGHT,
	done: STILL_LIGHT,
	asleep: STILL_LIGHT,
	failed: STILL_LIGHT,
};

/** The faded shadow is part of the sleeping pose, so it survives reduced motion like the shut eyes. */
const staticShadowVariants: Variants = {
	idle: STILL_SHADOW,
	working: STILL_SHADOW,
	done: STILL_SHADOW,
	asleep: { ...STILL_SHADOW, opacity: ASLEEP_SHADOW_OPACITY },
	failed: STILL_SHADOW,
};

/**
 * Below 20px the eyes sit fractionally further apart and a shade smaller, so the pair stays
 * legible as two eyes at table-cell sizes instead of merging into one mark.
 */
function eyeGeometry(size: number) {
	const isLarge = size >= 20;
	return {
		offsetX: isLarge ? 2.75 : 2.8,
		radiusX: isLarge ? 1.55 : 1.5,
		radiusY: isLarge ? 1.9 : 1.85,
	};
}

/**
 * The animation is the component's whole job, so the props that drive it are not
 * forwardable. Everything else about an `svg` still is, which is what lets a consumer
 * attach a `data-testid` or an event handler without wrapping the element.
 *
 * `fill` and `aria-hidden` are held back for a different reason than the rest: both have a
 * prop or a guarantee that already owns them, and forwarding either would give a caller a
 * second, quieter way to contradict it — `fill` beating `color`, or an avatar with no
 * accessible content being handed to assistive technology as though it had some.
 */
type ForwardedSvgProps = Omit<
	React.ComponentPropsWithoutRef<typeof motion.svg>,
	| "color"
	| "fill"
	| "aria-hidden"
	| "children"
	| "variants"
	| "animate"
	| "initial"
	| "custom"
	| "style"
	| "viewBox"
	| "width"
	| "height"
>;

/**
 * How the silhouette is painted. `flat` is one solid fill; `vinyl` is a soft satin body with a
 * highlight that holds still and a shadow on the ground.
 */
export type AgentAvatarFinish = "flat" | "vinyl";

/** Props for the {@link AgentAvatar} component. */
export type AgentAvatarProps = ForwardedSvgProps & {
	/** Which silhouette to draw. Carries the agent's identity, so keep it stable per agent. */
	shape: AgentAvatarShape;
	/** Fill for the silhouette. Any CSS colour; {@link AGENT_AVATAR_COLORS} is the default palette. */
	color?: string;
	/** What the agent is doing. Defaults to `"idle"`. */
	state?: AgentAvatarState;
	/**
	 * How the silhouette is painted. Defaults to `"flat"`.
	 *
	 * Both finishes share every timing and pose. Vinyl moves the body inside the svg instead
	 * of the svg itself, so its light and its ground shadow stay put while the body moves. The
	 * shadow is drawn only above 18px.
	 */
	finish?: AgentAvatarFinish;
	/** Width and height in pixels. Defaults to `24`. */
	size?: number;
	/**
	 * Whether to play the ambient animation. Defaults to `true`.
	 *
	 * Not Motion's `animate`. This is a plain switch, and `state` still chooses the pose.
	 * Turning it off gives the same rendering a reduced-motion reader gets: the poses are
	 * kept and the movement dropped, so a sleeping agent still has its eyes shut. Reduced
	 * motion always wins, because this can only take movement away, never put it back.
	 *
	 * Worth reaching for where the avatars are incidental rather than the subject. A long
	 * table of them blinking and glancing is a lot of motion for a column that is only
	 * carrying identity.
	 */
	animate?: boolean;
	/**
	 * Seconds to offset the ambient eye clock by, so a row of avatars does not blink in
	 * unison. Defaults to `0`.
	 *
	 * A prop rather than a random offset chosen internally: a random value differs between
	 * the server and client render and would trip a hydration mismatch. Derive it from
	 * something stable, such as the agent's position in the list.
	 */
	delay?: number;
	className?: string;
};

/**
 * A small character standing in for a background agent, whose behaviour reports what the
 * agent is doing.
 *
 * Idle it blinks and glances about; working it rocks on the spot and looks around more
 * often; on finishing it hops twice; switched off it sleeps with its eyes shut; after a
 * failed run it stands still and looks down. The states are poses as much as animations,
 * so the avatar still says something under `prefers-reduced-motion`, where all movement is
 * dropped but the sleeping and failed poses are kept.
 *
 * Purely decorative, and marked `aria-hidden` accordingly — the surface around it is
 * responsible for saying "Working now" or "Done" in text. It does not announce state on its
 * own, and should not be the only thing that does.
 *
 * @param props.shape - Which silhouette to draw.
 * @param props.color - Fill for the silhouette. Defaults to the first of {@link AGENT_AVATAR_COLORS}.
 * @param props.state - What the agent is doing. Defaults to `"idle"`.
 * @param props.finish - How the silhouette is painted. Defaults to `"flat"`.
 * @param props.size - Width and height in pixels. Defaults to `24`.
 * @param props.delay - Seconds to offset the ambient eye clock by. Defaults to `0`.
 * @param props.animate - Whether to play the ambient animation. Defaults to `true`.
 */
function AgentAvatar({
	shape,
	color = AGENT_AVATAR_COLORS[0],
	state = "idle",
	finish = "flat",
	size = 24,
	delay = 0,
	animate = true,
	className,
	...props
}: AgentAvatarProps) {
	// Resolves on the client's first render and is false on the server, so it selects the
	// transition rather than the markup — both variant sets share the same resting values,
	// which is what keeps the two renders identical.
	const prefersReducedMotion = useReducedMotion() ?? false;

	// Both reasons produce the same rendering, so they collapse into one switch. The order
	// matters only in what it forbids: `animate` can subtract movement but never add it
	// back, so a caller cannot animate an avatar for a reader who asked not to be.
	const isStill = prefersReducedMotion || !animate;

	// `delay` staggers the ambient loop's *start*; it is not a pause to serve on every
	// transition. Motion re-reads a variant's transition each time `animate` changes, so
	// leaving the offset in place would hold the eyes still for `delay` seconds every time
	// an agent started or finished — and worse, leave them shut on waking, because `asleep`
	// parks them closed. Once the loops are running they are already out of phase with each
	// other, which is all the offset was ever for.
	// Latched off the first `state` change rather than read back from a ref during
	// render: `state` is the prop that makes Motion re-read the transition, so it
	// is the moment the offset is spent. Adjusting state during render is React's
	// documented answer here; a ref read would be wrong on a replayed render.
	const [initialState] = React.useState(state);
	const [hasAnimated, setHasAnimated] = React.useState(false);
	if (!hasAnimated && state !== initialState) {
		setHasAnimated(true);
	}
	const staggerDelay = hasAnimated ? 0 : delay;

	// Called on every render, whichever the finish, so the hook order never depends on a prop.
	// An id from React rather than a counter, so two avatars on one page never share a
	// gradient and the server and client renders agree on it.
	const id = React.useId();

	const { offsetX, radiusX, radiusY } = eyeGeometry(size);

	const body = isStill ? staticBodyVariants : bodyVariants;
	const eyes = isStill ? staticEyesVariants : eyesVariants;
	const eye = isStill ? staticEyeVariants : eyeVariants;

	const eyeLayer = (
		// Two layers on one clock: the group glances, the ellipses inside it blink.
		// `custom` is restated on each child rather than left to propagate, so the delay
		// a reader sees on the parent is demonstrably the one every layer receives.
		<motion.g
			data-slot="agent-avatar-eyes"
			data-testid="agent-avatar-eyes"
			variants={eyes}
			custom={staggerDelay}
			style={SVG_SELF_ORIGIN}
		>
			<motion.ellipse
				data-slot="agent-avatar-eye"
				data-testid="agent-avatar-eye"
				variants={eye}
				custom={staggerDelay}
				style={SVG_SELF_ORIGIN}
				cx={12 - offsetX}
				cy={11.6}
				rx={radiusX}
				ry={radiusY}
				fill="#fff"
			/>
			<motion.ellipse
				data-slot="agent-avatar-eye"
				data-testid="agent-avatar-eye"
				variants={eye}
				custom={staggerDelay}
				style={SVG_SELF_ORIGIN}
				cx={12 + offsetX}
				cy={11.6}
				rx={radiusX}
				ry={radiusY}
				fill="#fff"
			/>
		</motion.g>
	);

	if (finish === "flat") {
		return (
			<motion.svg
				aria-hidden
				data-slot="agent-avatar"
				data-state={state}
				className={cn("block shrink-0", className)}
				width={size}
				height={size}
				viewBox="0 0 24 24"
				fill={color}
				style={BODY_ORIGIN}
				variants={body}
				animate={state}
				custom={staggerDelay}
				{...props}
			>
				{SHAPES[shape]}
				{eyeLayer}
			</motion.svg>
		);
	}

	const clipId = `${id}clip`;
	const fillId = `${id}fill`;
	const shadeId = `${id}shade`;
	const highlightId = `${id}highlight`;

	// The root never moves in this finish. It still takes `animate`, because that is what
	// hands the state down to the layers that do.
	return (
		<motion.svg
			aria-hidden
			data-slot="agent-avatar"
			data-state={state}
			className={cn("block shrink-0", className)}
			width={size}
			height={size}
			viewBox="0 0 24 24"
			style={VINYL_ROOT_STYLE}
			animate={state}
			custom={staggerDelay}
			{...props}
		>
			<defs>
				<clipPath id={clipId}>{SHAPES[shape]}</clipPath>
				{/* Lit from the top left. `color-mix` rather than arithmetic on a hex value,
				    because `color` takes any CSS colour. */}
				<radialGradient id={fillId} cx="0.36" cy="0.3" r="0.85">
					<stop
						offset="0"
						style={{ stopColor: `color-mix(in srgb, ${color}, white 10%)` }}
					/>
					<stop offset="0.45" style={{ stopColor: color }} />
					<stop
						offset="1"
						style={{ stopColor: `color-mix(in srgb, ${color}, black 11%)` }}
					/>
				</radialGradient>
				<radialGradient id={shadeId} cx="0.5" cy="1.1" r="0.6">
					<stop offset="0.3" stopColor="#000" stopOpacity="0.07" />
					<stop offset="1" stopColor="#000" stopOpacity="0" />
				</radialGradient>
				<radialGradient id={highlightId} cx="0.5" cy="0.5" r="0.5">
					<stop offset="0" stopColor="#fff" stopOpacity="0.2" />
					<stop offset="1" stopColor="#fff" stopOpacity="0" />
				</radialGradient>
			</defs>
			{size > VINYL_SHADOW_MIN_SIZE && (
				<motion.ellipse
					data-slot="agent-avatar-shadow"
					data-testid="agent-avatar-shadow"
					variants={isStill ? staticShadowVariants : shadowVariants}
					style={SVG_SELF_ORIGIN}
					cx={12}
					cy={23.4}
					rx={7}
					ry={0.9}
					fill="#000"
					// Stated up front because Motion starts a fade from the attribute it reads, and
					// an SVG child has none until something sets it.
					opacity={SHADOW_OPACITY}
				/>
			)}
			{/* The body carries the flat finish's rock, hop and breath; the turn inside
			    it carries the glance. Two groups because each state animates some of the same
			    properties on two different clocks. */}
			<motion.g
				data-slot="agent-avatar-body"
				data-testid="agent-avatar-body"
				variants={body}
				style={VINYL_PIVOT}
				opacity={1}
			>
				<motion.g
					data-slot="agent-avatar-turn"
					data-testid="agent-avatar-turn"
					variants={isStill ? staticTurnVariants : turnVariants}
					custom={staggerDelay}
					style={VINYL_PIVOT}
				>
					<g clipPath={`url(#${clipId})`}>
						<rect width="24" height="24" fill={`url(#${fillId})`} />
						<rect width="24" height="24" fill={`url(#${shadeId})`} />
						<motion.g
							data-slot="agent-avatar-light"
							data-testid="agent-avatar-light"
							variants={isStill ? staticLightVariants : lightVariants}
							style={VINYL_PIVOT}
						>
							<motion.g
								data-slot="agent-avatar-sheen"
								data-testid="agent-avatar-sheen"
								variants={isStill ? staticSheenVariants : sheenVariants}
								custom={staggerDelay}
							>
								<ellipse
									cx="8.2"
									cy="6.4"
									rx="5.4"
									ry="3.2"
									fill={`url(#${highlightId})`}
									transform="rotate(-28 8.2 6.4)"
								/>
							</motion.g>
						</motion.g>
					</g>
					{eyeLayer}
				</motion.g>
			</motion.g>
		</motion.svg>
	);
}

export { AgentAvatar };
